import { useState, useEffect, useCallback, useMemo } from "react";
import { BATCH_SIZE } from "../constants";

/**
 * Custom hook for infinite horizontal scroll functionality
 * Handles batch loading of columns when scrolling near the end
 * 
 * @param {Object} options - Configuration options
 * @param {number} options.totalItems - Total number of items available
 * @param {number} options.batchSize - Number of items to load per batch
 * @param {boolean} options.isVirtualScrollingEnabled - Whether virtual scrolling is enabled
 * @param {string} options.containerSelector - CSS selector for the scrollable container
 * @returns {Object} - Hook state and handlers
 */
export const useInfiniteScroll = ({
  totalItems = 0,
  batchSize = BATCH_SIZE,
  isVirtualScrollingEnabled = false,
  containerSelector = '[data-scroll-container]'
}) => {
  const [visibleItems, setVisibleItems] = useState(batchSize);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");

  // Reset visible items when total items change
  useEffect(() => {
    setVisibleItems(batchSize);
    setIsLoading(false);
    setLoadingMessage("");
  }, [totalItems, batchSize]);

  // Main scroll handler
  const handleScroll = useCallback((event) => {
    // Prevent multiple executions if already loading or all items loaded
    if (isLoading || visibleItems >= totalItems) return;
    
    const target = event.target;
    const scrollLeft = target.scrollLeft;
    const scrollWidth = target.scrollWidth;
    const clientWidth = target.clientWidth;
    
    // Calculate if we're near the end
    const remainingScroll = scrollWidth - (scrollLeft + clientWidth);
    const threshold = 200; // Threshold for early loading
    
    // Only load if we're near the end
    if (remainingScroll <= threshold && visibleItems < totalItems) {
      setIsLoading(true);
      const newVisibleCount = Math.min(visibleItems + batchSize, totalItems);
      
      setLoadingMessage(`Loading more data... (${visibleItems + 1}-${newVisibleCount} of ${totalItems} columns)`);
      
      // Use requestAnimationFrame for better performance
      requestAnimationFrame(() => {
        setVisibleItems(newVisibleCount);
        requestAnimationFrame(() => {
          setLoadingMessage("");
          setIsLoading(false);
        });
      });
    }
  }, [visibleItems, totalItems, isLoading, batchSize]);

  // Enhanced scroll handler with throttling
  const enhancedScrollHandler = useMemo(() => {
    let lastTime = 0;
    let animationId = null;
    const throttleMs = 50; // Reduced throttle for more responsive detection
    
    return function enhancedScroll(event) {
      // Early exit if all content is already loaded
      if (visibleItems >= totalItems) return;
      
      const now = Date.now();
      if (now - lastTime >= throttleMs) {
        lastTime = now;
        
        // Cancel previous animation frame if it exists
        if (animationId) {
          cancelAnimationFrame(animationId);
        }
        
        // Use requestAnimationFrame for better performance
        animationId = requestAnimationFrame(() => {
          handleScroll(event);
          animationId = null;
        });
      }
    };
  }, [handleScroll, visibleItems, totalItems]);

  // Handler to check scroll position programmatically
  const checkScrollPosition = useCallback(() => {
    // Early exit if all content is already loaded
    if (isLoading || visibleItems >= totalItems) return;
    
    // Find the scrollable element
    const scrollableElement = document.querySelector(containerSelector);
    if (!scrollableElement) return;
    
    const scrollLeft = scrollableElement.scrollLeft;
    const scrollWidth = scrollableElement.scrollWidth;
    const clientWidth = scrollableElement.clientWidth;
    const remainingScroll = scrollWidth - (scrollLeft + clientWidth);
    
    // If we're near the end and haven't loaded more, trigger loading
    if (remainingScroll <= 300 && visibleItems < totalItems) {
      setIsLoading(true);
      const newVisibleCount = Math.min(visibleItems + batchSize, totalItems);
      
      setLoadingMessage(`Loading more data... (${visibleItems + 1}-${newVisibleCount} of ${totalItems} columns)`);
      
      requestAnimationFrame(() => {
        setVisibleItems(newVisibleCount);
        requestAnimationFrame(() => {
          setLoadingMessage("");
          setIsLoading(false);
        });
      });
    }
  }, [visibleItems, totalItems, isLoading, batchSize, containerSelector]);

  // Setup event listeners
  useEffect(() => {
    // If virtual scrolling is enabled, don't use traditional scroll handler
    if (isVirtualScrollingEnabled && totalItems > 50) {
      return;
    }
    
    // If all items are already visible, no need for scroll detection
    if (visibleItems >= totalItems) {
      return;
    }
    
    // Multiple event listeners for better scroll detection
    const handleScrollCapture = (event) => {
      // Only process if the event comes from an element with horizontal scroll
      if (event.target.scrollWidth > event.target.clientWidth) {
        enhancedScrollHandler(event);
      }
    };

    const handleMouseUp = () => {
      // Check scroll position when mouse is released (helps with drag scenarios)
      setTimeout(checkScrollPosition, 100);
    };

    const handleMouseLeave = () => {
      // Check scroll position when mouse leaves the window
      setTimeout(checkScrollPosition, 50);
    };

    const handleTouchEnd = () => {
      // Check scroll position after touch scroll ends
      setTimeout(checkScrollPosition, 100);
    };
    
    // Add multiple event listeners for comprehensive detection
    document.addEventListener("scroll", handleScrollCapture, { capture: true, passive: true });
    document.addEventListener("mouseup", handleMouseUp, { passive: true });
    document.addEventListener("mouseleave", handleMouseLeave, { passive: true });
    document.addEventListener("touchend", handleTouchEnd, { passive: true });
    
    // Periodic check as fallback - but only if we haven't loaded everything
    const intervalId = setInterval(() => {
      // Double-check inside the interval
      if (visibleItems >= totalItems) {
        clearInterval(intervalId);
        return;
      }
      checkScrollPosition();
    }, 500);
    
    return () => {
      document.removeEventListener("scroll", handleScrollCapture, { capture: true });
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("mouseleave", handleMouseLeave);
      document.removeEventListener("touchend", handleTouchEnd);
      clearInterval(intervalId);
    };
  }, [
    enhancedScrollHandler, 
    checkScrollPosition, 
    totalItems, 
    visibleItems, 
    isVirtualScrollingEnabled
  ]);

  return {
    visibleItems,
    isLoading,
    loadingMessage,
    checkScrollPosition,
    setVisibleItems,
    setIsLoading,
    setLoadingMessage
  };
};

export default useInfiniteScroll;
